import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';

// import { EnvironmentService } from '../../../services/enviroment.service';
// import { environment } from '../../../services/global-environment';;

import { environment } from '../../../services/global-environment';
import { DatabaseService } from '../../../services/database.service';
import { LogService } from '../../../services/Logging.service';
// 引入 ECharts
import * as echarts from 'echarts';

// 部門資料模型
interface Department {
  id: string;
  name: string;
  description?: string;
}

// 化學品資料模型
interface Chemical {
  id: string;
  chineseName: string;
  englishName: string;
  partNumber: string;
  unit: string;
  dangerLevel: number; // 1-低風險, 2-中風險, 3-高風險
  currentStock: number;
  warningLevel: number; // 庫存警戒值
}

// 部門化學品庫存模型
interface DepartmentChemicalStock {
  departmentId: string;
  departmentName: string;
  chemicalId: string;
  chemicalName: string;
  partNumber: string;
  currentStock: number;
  unit: string;
  dangerLevel: number;
  lastUpdated: string;
}

// 化學品使用記錄模型
interface ChemicalUsageRecord {
  id: string;
  chemicalId: string;
  chemicalName: string;
  departmentId: string;
  departmentName: string;
  date: string;
  quantity: number; // 正數表示增加, 負數表示減少
  stockAfterChange: number;
  totalStockAfterChange: number;
  note: string;
  recordedBy: string;
  unit: string;
}

// 過濾條件
interface FilterOptions {
  department: string;
  chemical: string;
  dateFrom: string;
  dateTo: string;
  recordType: 'all' | 'in' | 'out' | 'transfer';
}

@Component({
  selector: 'app-use-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './use-list.component.html',
  styleUrl: './use-list.component.scss'
})
export class UseListComponent implements OnInit, OnDestroy {
  // 資料來源
  departments: Department[] = [];
  chemicals: Chemical[] = [];
  departmentChemicalStocks: DepartmentChemicalStock[] = [];
  usageRecords: ChemicalUsageRecord[] = [];

  // 篩選與分頁
  filteredStocks: DepartmentChemicalStock[] = [];
  filteredRecords: ChemicalUsageRecord[] = [];
  filterOptions: FilterOptions = {
    department: '',
    chemical: '',
    dateFrom: '',
    dateTo: '',
    recordType: 'all'
  };

  // 視圖狀態
  loading: boolean = true;
  viewMode: 'department' | 'chemical' = 'department';
  selectedDepartment: string = '';
  selectedChemical: string = '';

  // 資料操作相關
  showRecordModal: boolean = false;
  operationType: 'in' | 'out' | 'transfer' = 'in';
  newRecord: {
    chemicalId: string;
    departmentId: string;
    targetDepartmentId?: string; // 轉移時使用
    quantity: number;
    note: string;
    date: string;
  } = this.getEmptyRecord();
  operationResult: { success: boolean; message: string } | null = null;

  targetDepartmentId: string = '';

  private apiUrl = environment.apiUrl;
  private companyId = environment.companyid;
  private userName = 'Demo User'; // 在實際應用中，這應該從認證服務獲取

  // 化學品歷史記錄相關
  showHistoryModal: boolean = false;
  selectedChemicalId: string = '';
  selectedDepartmentId: string = '';
  selectedChemicalName: string = '';
  selectedDepartmentName: string = '';
  chemicalHistoryRecords: ChemicalUsageRecord[] = [];
  historyChartPeriod: '1m' | '3m' | '6m' | '1y' = '1m';
  historyChart: echarts.ECharts | null = null;

  // 添加化學品統計數據對象
  selectedChemicalStats: {
    avgUsage: number;
    maxUsage: number;
    avgStock: number;
    maxStock: number;
    unit: string;
  } = {
    avgUsage: 0,
    maxUsage: 0,
    avgStock: 0,
    maxStock: 0,
    unit: ''
  };

  constructor(
    private http: HttpClient,
    private databaseService: DatabaseService,
    private logService: LogService
  ) {}

  ngOnInit(): void {
    this.loadInitialData();

    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    this.filterOptions.dateFrom = this.formatDateForInput(lastMonth);
    this.filterOptions.dateTo = this.formatDateForInput(today);
  }

  ngOnDestroy(): void {
    if (this.historyChart) {
      this.historyChart.dispose();
    }
    window.removeEventListener('resize', () => {});
  }

  // 載入初始資料
  loadInitialData(): void {
    this.loading = true;

    // 在實際應用中，應該使用Promise.all或forkJoin來同時請求多個API
    this.loadDepartments();
    this.loadChemicals();
    // 等待部門和化學品資料載入後再載入庫存和歷史記錄
    setTimeout(() => {
      this.loadDepartmentChemicalStocks();
    }, 500);
  }

  // 載入部門資料
  loadDepartments(): void {
    // 模擬從API載入部門資料
    this.departments = [
      { id: 'D001', name: '研發部', description: '產品研發與測試' },
      { id: 'D002', name: '品管部', description: '品質管理與檢測' },
      { id: 'D003', name: '生產部', description: '產品製造與組裝' },
      { id: 'D004', name: '實驗室', description: '物料研究與分析' },
      { id: 'D005', name: '維修部', description: '設備維護與修理' }
    ];

    // 實際應用中的API調用可能如下：
    /*
    this.databaseService.getDataByQuery({ companyId: this.companyId }, 100, "departments").subscribe({
      next: (response: any) => {
        if (response?.items?.length) {
          this.departments = response.items;
        }
      },
      error: (error) => {
        console.error('Error loading departments:', error);
        this.logService.Loginlog(this.companyId, 'Chemical', 'System', 'Error loading departments: ' + error.message, 'ERROR');
      }
    });
    */
  }

  // 載入化學品資料
  loadChemicals(): void {
    // 模擬從API載入化學品資料
    this.chemicals = [
      { id: 'C001', chineseName: '丙酮', englishName: 'Acetone', partNumber: 'CH-A001', unit: '公升', dangerLevel: 2, currentStock: 150, warningLevel: 20 },
      { id: 'C002', chineseName: '甲苯', englishName: 'Toluene', partNumber: 'CH-T002', unit: '公升', dangerLevel: 3, currentStock: 80, warningLevel: 30 },
      { id: 'C003', chineseName: '乙醇', englishName: 'Ethanol', partNumber: 'CH-E003', unit: '公升', dangerLevel: 1, currentStock: 200, warningLevel: 50 },
      { id: 'C004', chineseName: '異丙醇', englishName: 'Isopropyl Alcohol', partNumber: 'CH-I004', unit: '公升', dangerLevel: 1, currentStock: 120, warningLevel: 40 },
      { id: 'C005', chineseName: '硫酸', englishName: 'Sulfuric Acid', partNumber: 'CH-S005', unit: '公升', dangerLevel: 3, currentStock: 50, warningLevel: 15 }
    ];

    // 實際應用中的API調用可能如下：
    /*
    this.databaseService.getDataByQuery({ companyId: this.companyId }, 500, "chemicals").subscribe({
      next: (response: any) => {
        if (response?.items?.length) {
          this.chemicals = response.items;
        }
      },
      error: (error) => {
        console.error('Error loading chemicals:', error);
        this.logService.Loginlog(this.companyId, 'Chemical', 'System', 'Error loading chemicals: ' + error.message, 'ERROR');
      }
    });
    */
  }

  // 載入部門化學品庫存
  loadDepartmentChemicalStocks(): void {
    // 模擬從API載入部門化學品庫存資料
    this.departmentChemicalStocks = [];

    // 為每個部門創建庫存記錄
    for (const department of this.departments) {
      for (const chemical of this.chemicals) {
        // 模擬不同部門會使用不同的化學品，並有不同的庫存量
        let shouldAdd = Math.random() > 0.3; // 70% 的機率添加此記錄
        if (shouldAdd) {
          // 生成隨機庫存量，但不超過總庫存
          const maxStock = chemical.currentStock / 2;
          const stock = Math.floor(Math.random() * maxStock) + 1;

          this.departmentChemicalStocks.push({
            departmentId: department.id,
            departmentName: department.name,
            chemicalId: chemical.id,
            chemicalName: chemical.chineseName,
            partNumber: chemical.partNumber,
            currentStock: stock,
            unit: chemical.unit,
            dangerLevel: chemical.dangerLevel,
            lastUpdated: this.getRandomDate(30) // 隨機生成過去30天內的日期
          });
        }
      }
    }

    // 載入使用記錄
    this.loadUsageRecords();

    // 實際應用中的API調用可能如下：
    /*
    this.databaseService.getDataByQuery({ companyId: this.companyId }, 1000, "departmentChemicalStocks").subscribe({
      next: (response: any) => {
        if (response?.items?.length) {
          this.departmentChemicalStocks = response.items;
          this.applyStockFilters();
        }
        this.loadUsageRecords();
      },
      error: (error) => {
        console.error('Error loading department chemical stocks:', error);
        this.logService.Loginlog(this.companyId, 'Chemical', 'System', 'Error loading department chemical stocks: ' + error.message, 'ERROR');
        this.loadUsageRecords();
      }
    });
    */
  }

  // 載入使用記錄
  loadUsageRecords(): void {
    // 模擬從API載入使用記錄資料
    this.usageRecords = [];

    // 為每個部門化學品組合生成一些使用記錄
    for (const stock of this.departmentChemicalStocks) {
      // 每個庫存項目生成1-5條記錄
      const recordCount = Math.floor(Math.random() * 5) + 1;

      for (let i = 0; i < recordCount; i++) {
        // 隨機決定是入庫(+)還是出庫(-)
        const isIncoming = Math.random() > 0.5;
        const quantity = isIncoming
          ? Math.floor(Math.random() * 20) + 1
          : -(Math.floor(Math.random() * (stock.currentStock / 2)) + 1);

        const stockAfter = stock.currentStock - quantity; // 當前庫存是最終結果，所以要減去變化量來模擬歷史庫存

        // 找到對應的化學品
        const chemical = this.chemicals.find(c => c.id === stock.chemicalId);
        const totalStockAfter = chemical ? chemical.currentStock - quantity : 0;

        this.usageRecords.push({
          id: `R${Date.now()}${Math.floor(Math.random() * 1000)}`,
          chemicalId: stock.chemicalId,
          chemicalName: stock.chemicalName,
          departmentId: stock.departmentId,
          departmentName: stock.departmentName,
          date: this.getRandomDate(30), // 隨機生成過去30天內的日期
          quantity: quantity,
          stockAfterChange: stockAfter,
          totalStockAfterChange: totalStockAfter,
          note: isIncoming ? '例行補充庫存' : '日常使用',
          recordedBy: this.userName,
          unit: stock.unit
        });
      }
    }

    // 排序記錄，最新的在前面
    this.usageRecords.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    // 應用過濾器
    this.applyFilters();
    this.loading = false;

    // 實際應用中的API調用可能如下：
    /*
    const queryParams = {
      companyId: this.companyId,
      dateFrom: this.filterOptions.dateFrom,
      dateTo: this.filterOptions.dateTo
    };

    this.databaseService.getDataByQuery(queryParams, 1000, "chemicalUsageRecords").subscribe({
      next: (response: any) => {
        if (response?.items?.length) {
          this.usageRecords = response.items;
        }
        this.applyFilters();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading usage records:', error);
        this.logService.Loginlog(this.companyId, 'Chemical', 'System', 'Error loading usage records: ' + error.message, 'ERROR');
        this.loading = false;
      }
    });
    */
  }

  // 應用篩選器
  applyFilters(): void {
    console.log('應用過濾條件:', this.filterOptions);

    // 顯示載入中狀態
    this.loading = true;

    // 模擬API調用的延遲
    setTimeout(() => {
      // 過濾部門化學品庫存
      this.applyStockFilters();

      // 過濾使用記錄
      let result = [...this.usageRecords];

      // 篩選部門
      if (this.filterOptions.department) {
        console.log('按部門過濾:', this.filterOptions.department);
        result = result.filter(record => record.departmentId === this.filterOptions.department);
      }

      // 篩選化學品
      if (this.filterOptions.chemical) {
        console.log('按化學品過濾:', this.filterOptions.chemical);
        result = result.filter(record => record.chemicalId === this.filterOptions.chemical);
      }

      // 篩選日期範圍
      if (this.filterOptions.dateFrom) {
        const fromDate = new Date(this.filterOptions.dateFrom);
        console.log('按開始日期過濾:', fromDate);
        result = result.filter(record => new Date(record.date) >= fromDate);
      }

      if (this.filterOptions.dateTo) {
        const toDate = new Date(this.filterOptions.dateTo);
        toDate.setHours(23, 59, 59); // 設為當天結束時間
        console.log('按結束日期過濾:', toDate);
        result = result.filter(record => new Date(record.date) <= toDate);
      }

      // 篩選記錄類型 (入庫/出庫/轉移)
      if (this.filterOptions.recordType !== 'all') {
        console.log('按記錄類型過濾:', this.filterOptions.recordType);
        switch (this.filterOptions.recordType) {
          case 'in':
            result = result.filter(record => record.quantity > 0);
            break;
          case 'out':
            result = result.filter(record => record.quantity < 0);
            break;
          case 'transfer':
            result = result.filter(record => record.note.toLowerCase().includes('轉移') || record.note.toLowerCase().includes('transfer'));
            break;
        }
      }

      console.log('過濾後記錄數量:', result.length);
      this.filteredRecords = result;

      // 關閉載入中狀態
      this.loading = false;
    }, 300);
  }

  // 應用庫存過濾器
  applyStockFilters(): void {
    let result = [...this.departmentChemicalStocks];

    // 篩選部門
    if (this.selectedDepartment) {
      result = result.filter(stock => stock.departmentId === this.selectedDepartment);
    }

    // 篩選化學品
    if (this.selectedChemical) {
      result = result.filter(stock => stock.chemicalId === this.selectedChemical);
    }

    this.filteredStocks = result;
  }

  // 切換視圖模式
  switchViewMode(mode: 'department' | 'chemical'): void {
    this.viewMode = mode;
    this.applyStockFilters();
  }

  // 選擇部門
  selectDepartment(departmentId: string): void {
    this.selectedDepartment = departmentId;
    this.applyStockFilters();
  }

  // 選擇化學品
  selectChemical(chemicalId: string): void {
    this.selectedChemical = chemicalId;
    this.applyStockFilters();
  }

  // 開啟記錄操作模態框
  openRecordModal(operationType: 'in' | 'out' | 'transfer', itemId?: string): void {
    this.operationType = operationType;
    this.newRecord = this.getEmptyRecord();

    // 如果是從特定項目開啟，預先填充資料
    if (itemId) {
      const stock = this.departmentChemicalStocks.find(s =>
        s.chemicalId === itemId &&
        (!this.selectedDepartment || s.departmentId === this.selectedDepartment)
      );

      if (stock) {
        this.newRecord.chemicalId = stock.chemicalId;
        this.newRecord.departmentId = stock.departmentId;
      }
    }

    this.showRecordModal = true;
    this.operationResult = null;
  }

  // 關閉記錄操作模態框
  closeRecordModal(): void {
    this.showRecordModal = false;
  }

  // 提交新記錄
  submitRecord(): void {
    // 表單驗證
    if (!this.validateRecordForm()) {
      this.operationResult = {
        success: false,
        message: '請完成所有必填欄位'
      };
      return;
    }

    // 檢查庫存是否足夠（出庫或轉移時）
    if (this.operationType === 'out' || this.operationType === 'transfer') {
      const stock = this.departmentChemicalStocks.find(
        s => s.departmentId === this.newRecord.departmentId &&
             s.chemicalId === this.newRecord.chemicalId
      );

      if (!stock || stock.currentStock < this.newRecord.quantity) {
        this.operationResult = {
          success: false,
          message: '庫存不足，無法執行此操作'
        };
        return;
      }
    }

    // 根據操作類型調整數量符號
    let quantity = this.newRecord.quantity;
    if (this.operationType === 'out') {
      quantity = -quantity; // 出庫為負數
    }

    // 更新部門化學品庫存
    this.updateDepartmentChemicalStock(
      this.newRecord.departmentId,
      this.newRecord.chemicalId,
      quantity
    );

    // 如果是轉移，還需要更新目標部門的庫存
    if (this.operationType === 'transfer' && this.newRecord.targetDepartmentId) {
      this.updateDepartmentChemicalStock(
        this.newRecord.targetDepartmentId,
        this.newRecord.chemicalId,
        this.newRecord.quantity // 目標部門增加相同數量
      );
    }

    // 更新總庫存
    const chemical = this.chemicals.find(c => c.id === this.newRecord.chemicalId);
    if (chemical) {
      if (this.operationType !== 'transfer') {
        // 轉移不影響總庫存，僅入庫和出庫會改變總庫存
        chemical.currentStock += quantity;
      }
    }

    // 創建使用記錄
    this.createUsageRecord(quantity);

    // 如果是轉移，為目標部門也創建一條記錄
    if (this.operationType === 'transfer' && this.newRecord.targetDepartmentId) {
      this.createTransferTargetRecord(this.newRecord.quantity);
    }

    // 展示操作結果
    this.operationResult = {
      success: true,
      message: '操作成功完成'
    };

    // 重新應用過濾器
    this.applyFilters();

    // 延遲後關閉模態框
    setTimeout(() => {
      this.closeRecordModal();
    }, 2000);
  }

  // 更新部門化學品庫存
  private updateDepartmentChemicalStock(departmentId: string, chemicalId: string, quantity: number): void {
    // 查找現有庫存
    let stock = this.departmentChemicalStocks.find(
      s => s.departmentId === departmentId && s.chemicalId === chemicalId
    );

    const department = this.departments.find(d => d.id === departmentId);
    const chemical = this.chemicals.find(c => c.id === chemicalId);

    if (!department || !chemical) return;

    if (stock) {
      // 更新現有庫存
      stock.currentStock += quantity;
      stock.lastUpdated = new Date().toISOString().split('T')[0];
    } else {
      // 創建新庫存記錄
      this.departmentChemicalStocks.push({
        departmentId: departmentId,
        departmentName: department.name,
        chemicalId: chemicalId,
        chemicalName: chemical.chineseName,
        partNumber: chemical.partNumber,
        currentStock: quantity,
        unit: chemical.unit,
        dangerLevel: chemical.dangerLevel,
        lastUpdated: new Date().toISOString().split('T')[0]
      });
    }

    // 實際應用中的API調用可能如下：
    /*
    const updateData = {
      companyId: this.companyId,
      departmentId: departmentId,
      chemicalId: chemicalId,
      quantity: quantity,
      lastUpdated: new Date().toISOString()
    };

    this.http.post(`${this.apiUrl}/api/chemical/updateStock`, updateData).subscribe({
      next: (response: any) => {
        if (response.success) {
          console.log('Stock updated successfully');
        } else {
          console.error('Failed to update stock:', response.message);
        }
      },
      error: (error) => {
        console.error('Error updating stock:', error);
      }
    });
    */
  }

  // 創建使用記錄
  private createUsageRecord(quantity: number): void {
    const department = this.departments.find(d => d.id === this.newRecord.departmentId);
    const chemical = this.chemicals.find(c => c.id === this.newRecord.chemicalId);

    if (!department || !chemical) return;

    // 找到相應的庫存
    const stock = this.departmentChemicalStocks.find(
      s => s.departmentId === this.newRecord.departmentId && s.chemicalId === this.newRecord.chemicalId
    );

    // 創建記錄
    const newRecord: ChemicalUsageRecord = {
      id: `R${Date.now()}`,
      chemicalId: chemical.id,
      chemicalName: chemical.chineseName,
      departmentId: department.id,
      departmentName: department.name,
      date: this.newRecord.date || new Date().toISOString().split('T')[0],
      quantity: quantity,
      stockAfterChange: stock ? stock.currentStock : quantity,
      totalStockAfterChange: chemical.currentStock,
      note: this.operationType === 'transfer'
        ? `轉移至${this.getDepartmentName(this.newRecord.targetDepartmentId || '')}: ${this.newRecord.note}`
        : this.newRecord.note,
      recordedBy: this.userName,
      unit: chemical.unit
    };

    // 添加到記錄列表
    this.usageRecords.unshift(newRecord);

    // 實際應用中的API調用可能如下：
    /*
    this.http.post(`${this.apiUrl}/api/chemical/addUsageRecord`, newRecord).subscribe({
      next: (response: any) => {
        if (response.success) {
          console.log('Usage record created successfully');
        } else {
          console.error('Failed to create usage record:', response.message);
        }
      },
      error: (error) => {
        console.error('Error creating usage record:', error);
      }
    });
    */
  }

  // 為轉移目標創建記錄
  private createTransferTargetRecord(quantity: number): void {
    if (!this.newRecord.targetDepartmentId) return;

    const targetDepartment = this.departments.find(d => d.id === this.newRecord.targetDepartmentId);
    const chemical = this.chemicals.find(c => c.id === this.newRecord.chemicalId);
    const sourceDepartment = this.departments.find(d => d.id === this.newRecord.departmentId);

    if (!targetDepartment || !chemical || !sourceDepartment) return;

    // 找到相應的庫存
    const stock = this.departmentChemicalStocks.find(
      s => s.departmentId === this.newRecord.targetDepartmentId && s.chemicalId === this.newRecord.chemicalId
    );

    // 創建記錄
    const newRecord: ChemicalUsageRecord = {
      id: `R${Date.now() + 1}`,
      chemicalId: chemical.id,
      chemicalName: chemical.chineseName,
      departmentId: targetDepartment.id,
      departmentName: targetDepartment.name,
      date: this.newRecord.date || new Date().toISOString().split('T')[0],
      quantity: quantity, // 目標部門增加，所以是正數
      stockAfterChange: stock ? stock.currentStock : quantity,
      totalStockAfterChange: chemical.currentStock,
      note: `從${sourceDepartment.name}轉移: ${this.newRecord.note}`,
      recordedBy: this.userName,
      unit: chemical.unit
    };

    // 添加到記錄列表
    this.usageRecords.unshift(newRecord);

    // 實際應用中的API調用可能如下略過
  }

  // 獲取空白記錄模板
  getEmptyRecord(): any {
    return {
      chemicalId: '',
      departmentId: this.selectedDepartment || '',
      targetDepartmentId: '',
      quantity: 0,
      note: '',
      date: new Date().toISOString().split('T')[0]
    };
  }

  // 驗證記錄表單
  validateRecordForm(): boolean {
    if (!this.newRecord.chemicalId || !this.newRecord.departmentId || !this.newRecord.quantity || this.newRecord.quantity <= 0) {
      return false;
    }

    // 如果是轉移，必須有目標部門
    if (this.operationType === 'transfer' && !this.newRecord.targetDepartmentId) {
      return false;
    }

    // 如果是轉移，來源和目標部門不能相同
    if (this.operationType === 'transfer' && this.newRecord.departmentId === this.newRecord.targetDepartmentId) {
      return false;
    }

    return true;
  }

  // 根據ID獲取部門名稱
  getDepartmentName(id: string): string {
    const department = this.departments.find(d => d.id === id);
    return department ? department.name : '未知部門';
  }

  // 獲取化學品的危險等級標識
  getDangerLevelClass(level: number): string {
    switch (level) {
      case 3: return 'danger-high';
      case 2: return 'danger-medium';
      case 1: return 'danger-low';
      default: return '';
    }
  }

  // 獲取庫存狀態標識
  getStockStatusClass(stock: DepartmentChemicalStock): string {
    const chemical = this.chemicals.find(c => c.id === stock.chemicalId);
    if (!chemical) return '';

    if (stock.currentStock <= 0) return 'stock-empty';
    if (stock.currentStock <= chemical.warningLevel) return 'stock-warning';
    return 'stock-normal';
  }

  // 格式化日期為輸入框格式 (YYYY-MM-DD)
  formatDateForInput(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // 產生隨機日期
  getRandomDate(daysBack: number): string {
    const today = new Date();
    const pastDate = new Date();
    pastDate.setDate(today.getDate() - Math.floor(Math.random() * daysBack));
    return this.formatDateForInput(pastDate);
  }

  onTargetDepartmentChange(departmentId: string): void {
    this.targetDepartmentId = departmentId;
    // Add any additional logic here if needed
  }

  // 查看化學品歷史記錄
  viewChemicalHistory(chemicalId: string, departmentId: string): void {
    this.selectedChemicalId = chemicalId;
    this.selectedDepartmentId = departmentId;

    // 找到化學品和部門名稱
    const chemical = this.chemicals.find(c => c.id === chemicalId);
    const department = this.departments.find(d => d.id === departmentId);

    if (chemical && department) {
      this.selectedChemicalName = chemical.chineseName;
      this.selectedDepartmentName = department.name;

      // 過濾出該化學品和部門的所有記錄
      this.loadChemicalHistoryData(chemicalId, departmentId);

      // 顯示模態框
      this.showHistoryModal = true;

      // 必須延遲初始化 ECharts，確保 DOM 已經準備好
      setTimeout(() => {
        this.initHistoryChart();
      }, 100);
    }
  }

  // 載入化學品歷史數據
  loadChemicalHistoryData(chemicalId: string, departmentId: string, period: '1m' | '3m' | '6m' | '1y' = '1m'): void {
    // 設置當前選中的時期
    this.historyChartPeriod = period;

    // 計算起始日期
    const today = new Date();
    let fromDate = new Date();

    switch(period) {
      case '1m':
        fromDate.setMonth(today.getMonth() - 1);
        break;
      case '3m':
        fromDate.setMonth(today.getMonth() - 3);
        break;
      case '6m':
        fromDate.setMonth(today.getMonth() - 6);
        break;
      case '1y':
        fromDate.setFullYear(today.getFullYear() - 1);
        break;
    }

    // 過濾記錄
    this.chemicalHistoryRecords = this.usageRecords.filter(record =>
      record.chemicalId === chemicalId &&
      record.departmentId === departmentId &&
      new Date(record.date) >= fromDate
    );

    // 按日期排序（從早到晚）以便於圖表顯示
    this.chemicalHistoryRecords.sort((a, b) =>
      new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    // 計算統計數據
    this.calculateChemicalStats(chemicalId, departmentId);

    // 如果圖表已經初始化，則更新圖表
    if (this.historyChart) {
      this.updateHistoryChart();
    }
  }

  // 初始化歷史圖表
  initHistoryChart(): void {
    const chartElement = document.getElementById('historyChart');

    if (!chartElement) {
      console.error('historyChart element not found');
      return;
    }

    // 初始化 ECharts 實例
    this.historyChart = echarts.init(chartElement);

    // 設置自適應
    window.addEventListener('resize', () => {
      if (this.historyChart) {
        this.historyChart.resize();
      }
    });

    // 更新圖表數據
    this.updateHistoryChart();
  }

  // 更新歷史圖表
  updateHistoryChart(): void {
    if (!this.historyChart) return;

    // 預處理數據
    const dates: string[] = [];
    const stockValues: number[] = [];
    const inflows: number[] = [];
    const outflows: number[] = [];

    // 生成一系列日期作為 x 軸（從起始日期到當前日期）
    const startDate = new Date(this.chemicalHistoryRecords[0]?.date || new Date());
    const endDate = new Date();
    const dateMap = new Map<string, {stockValue: number, inflow: number, outflow: number}>();

    // 初始化日期映射
    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = this.formatDateForInput(d);
      dates.push(dateKey);
      dateMap.set(dateKey, {stockValue: 0, inflow: 0, outflow: 0});
    }

    // 填充實際數據
    let lastStockValue = 0;
    this.chemicalHistoryRecords.forEach(record => {
      const dateKey = record.date;
      lastStockValue = record.stockAfterChange;

      if (dateMap.has(dateKey)) {
        const entry = dateMap.get(dateKey)!;
        entry.stockValue = record.stockAfterChange;

        if (record.quantity > 0) {
          entry.inflow += record.quantity;
        } else if (record.quantity < 0) {
          entry.outflow += Math.abs(record.quantity);
        }

        dateMap.set(dateKey, entry);
      }
    });

    // 填充沒有記錄的日期的庫存值（使用最近的值）
    let currentStock = 0;
    dates.forEach(date => {
      const entry = dateMap.get(date)!;
      if (entry.stockValue > 0) {
        currentStock = entry.stockValue;
      } else {
        entry.stockValue = currentStock;
      }

      stockValues.push(entry.stockValue);
      inflows.push(entry.inflow);
      outflows.push(-entry.outflow); // 使用負值表示出庫
    });

    // 設置圖表選項
    const option = {
      title: {
        text: '化學品庫存變化趨勢',
        left: 'center'
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          label: {
            backgroundColor: '#6a7985'
          }
        },
        formatter: (params: any) => {
          const date = params[0].axisValue;
          let html = `<div><b>${date}</b></div>`;

          params.forEach((param: any) => {
            let value = param.value;
            const color = param.color;
            const marker = `<span style="display:inline-block;margin-right:4px;border-radius:10px;width:10px;height:10px;background-color:${color};"></span>`;

            if (param.seriesName === '入庫量' && value > 0) {
              html += `<div>${marker}${param.seriesName}: +${value} ${this.selectedChemicalStats.unit}</div>`;
            } else if (param.seriesName === '出庫量' && value < 0) {
              html += `<div>${marker}${param.seriesName}: ${value} ${this.selectedChemicalStats.unit}</div>`;
            } else if (param.seriesName === '庫存量') {
              html += `<div>${marker}${param.seriesName}: ${value} ${this.selectedChemicalStats.unit}</div>`;
            }
          });

          return html;
        }
      },
      legend: {
        data: ['庫存量', '入庫量', '出庫量'],
        top: 30
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: dates
      },
      yAxis: {
        type: 'value',
        name: `數量 (${this.selectedChemicalStats.unit})`
      },
      series: [
        {
          name: '庫存量',
          type: 'line',
          stack: 'Total',
          smooth: true,
          lineStyle: {
            width: 3,
            color: '#5470c6'
          },
          showSymbol: false,
          areaStyle: {
            opacity: 0.2
          },
          data: stockValues
        },
        {
          name: '入庫量',
          type: 'bar',
          stack: 'flow',
          barWidth: '60%',
          itemStyle: {
            color: '#91cc75'
          },
          data: inflows
        },
        {
          name: '出庫量',
          type: 'bar',
          stack: 'flow',
          barWidth: '60%',
          itemStyle: {
            color: '#ee6666'
          },
          data: outflows
        }
      ]
    };

    // 應用配置
    this.historyChart.setOption(option);
  }

  // 設置歷史圖表時段
  setHistoryChartPeriod(period: '1m' | '3m' | '6m' | '1y'): void {
    this.loadChemicalHistoryData(this.selectedChemicalId, this.selectedDepartmentId, period);
  }

  // 計算化學品統計數據
  calculateChemicalStats(chemicalId: string, departmentId: string): void {
    const chemical = this.chemicals.find(c => c.id === chemicalId);
    if (!chemical) return;

    // 過濾出該化學品和部門的所有記錄
    const records = this.usageRecords.filter(record =>
      record.chemicalId === chemicalId &&
      record.departmentId === departmentId
    );

    if (records.length === 0) {
      this.selectedChemicalStats = {
        avgUsage: 0,
        maxUsage: 0,
        avgStock: 0,
        maxStock: 0,
        unit: chemical.unit
      };
      return;
    }

    // 計算平均使用量（僅考慮出庫記錄）
    const usageRecords = records.filter(record => record.quantity < 0);
    let avgUsage = 0;
    if (usageRecords.length > 0) {
      const totalUsage = usageRecords.reduce((sum, record) => sum + Math.abs(record.quantity), 0);
      avgUsage = Math.round((totalUsage / usageRecords.length) * 100) / 100;
    }

    // 計算最大使用量
    const maxUsage = usageRecords.length > 0
      ? Math.abs(Math.min(...usageRecords.map(record => record.quantity)))
      : 0;

    // 計算平均庫存量
    const totalStock = records.reduce((sum, record) => sum + record.stockAfterChange, 0);
    const avgStock = Math.round((totalStock / records.length) * 100) / 100;

    // 計算最大庫存量
    const maxStock = Math.max(...records.map(record => record.stockAfterChange));

    this.selectedChemicalStats = {
      avgUsage,
      maxUsage,
      avgStock,
      maxStock,
      unit: chemical.unit
    };
  }

  // 關閉歷史記錄模態框
  closeHistoryModal(): void {
    this.showHistoryModal = false;
    if (this.historyChart) {
      this.historyChart.dispose();
      this.historyChart = null;
    }
  }

  // 重置過濾條件
  resetFilters(): void {
    const today = new Date();
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);

    this.filterOptions = {
      department: '',
      chemical: '',
      dateFrom: this.formatDateForInput(lastMonth),
      dateTo: this.formatDateForInput(today),
      recordType: 'all'
    };

    this.applyFilters();
  }
}
